import React from "react";

/**
 * App-wide error boundary.
 *
 * In React 19, any uncaught error thrown during render OR during an effect's
 * cleanup function propagates up and unmounts the entire React root — the user
 * sees a blank white page and the only recovery is a hard refresh. That was the
 * exact symptom being reported ("screen goes blank on every route change,
 * refresh brings it back").
 *
 * Wrapping the router in this boundary means an error on ONE route no longer
 * blows up the whole SPA. We also reset the boundary automatically whenever the
 * URL changes, so navigating away from the broken route heals the app without a
 * refresh.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, pathname: props.pathname };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    // Auto-reset when the URL changes so a broken route doesn't lock the app.
    if (prevState.error && nextProps.pathname !== prevState.pathname) {
      return { error: null, pathname: nextProps.pathname };
    }
    if (nextProps.pathname !== prevState.pathname) {
      return { pathname: nextProps.pathname };
    }
    return null;
  }

  componentDidCatch(error, info) {
    // Log once so we can see it in DevTools without spamming.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  handleReload = () => {
    try {
      window.location.reload();
    } catch { /* noop */ }
  };

  handleGoHome = () => {
    try {
      window.location.assign("/");
    } catch { /* noop */ }
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        role="alert"
        data-testid="app-error-boundary"
        style={{
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "48px 20px",
          textAlign: "center",
          color: "#0f172a",
          background: "#F4F6FA",
        }}
      >
        <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 12 }}>
          Something went wrong on this page.
        </div>
        <div style={{ fontSize: 14, color: "#475569", maxWidth: 460, marginBottom: 24 }}>
          The page hit an unexpected error. You can go back to the homepage or reload — your work is safe.
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <button
            type="button"
            onClick={this.handleGoHome}
            data-testid="error-boundary-home-btn"
            style={{
              padding: "10px 20px",
              borderRadius: 999,
              background: "#2C66E3",
              color: "#fff",
              fontWeight: 700,
              fontSize: 14,
              border: "none",
              cursor: "pointer",
            }}
          >
            Back to home
          </button>
          <button
            type="button"
            onClick={this.handleReload}
            data-testid="error-boundary-reload-btn"
            style={{
              padding: "10px 20px",
              borderRadius: 999,
              background: "#fff",
              color: "#0f172a",
              fontWeight: 700,
              fontSize: 14,
              border: "1.5px solid #cbd5e1",
              cursor: "pointer",
            }}
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
